import { Box, Button, Divider, Flex, Text, Link, Menu as ChakraMenu, MenuButton, MenuList, MenuItem, IconButton, Skeleton } from '@chakra-ui/react';
import { ReactComponent as ArrowsIcon } from 'assets/arrows-icon.svg';
import { ReactComponent as ActivityIcon } from 'assets/activity-icon.svg';
import { ReactComponent as SquaresIcon } from 'assets/squares-icon.svg';
import { ReactComponent as AssetsIcon } from 'assets/assets-icon.svg';
import { ReactComponent as SettingsIcon } from 'assets/settings-icon.svg';
import { ReactComponent as SwitchAccountIcon } from 'assets/switch-account-icon.svg';
import { ApplicationRoutes } from '../../../utils/routes';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as ExternalIcon } from 'assets/external-icon.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHasLoaded } from 'hooks/useHasLoaded';
import { GlobalContext } from '../../../providers/Global';
import { getAccountData } from '../../../api/userAPI';
import { getExplorerUrl } from '../../../utils/explorerUrl';
import copyToClipboard from 'copy-to-clipboard';
import { useToast } from 'hooks/useToast';
import HashBasedAvatar from 'components/Avatar';
import { getAvatarUID } from 'utils/avatar';
import { useMediaQuery } from "react-responsive";
import { ModalType, SCREEN_LG } from "utils/constants";
import { ReactComponent as ArrowDown } from 'assets/arrow-down-icon.svg';
import { ReactComponent as WalletFundIcon } from 'assets/wallet-fund-icon.svg';
import { ReactComponent as HederaLogo } from 'assets/hedera-logo.svg';
import AnimatedNumber from 'components/AnimatedNumber';

const menuItems = [
  {
    name: 'Dashboard',
    route: 'account',
    selectedIcon: SquaresIcon,
  },
  {
    name: 'Assets',
    route: 'assets',
    selectedIcon: AssetsIcon,
  },
  {
    name: 'Transactions',
    route: 'transactions',
    selectedIcon: ArrowsIcon,
  },
  {
    name: 'Activity',
    route: 'activity',
    selectedIcon: ActivityIcon,
  },
  {
    name: 'Settings',
    route: 'settings',
    selectedIcon: SettingsIcon,
  },
];

const Menu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { multisigAccountId, walletId } = useParams();
  const { updateBalance, balance, currentAccount, connectedNetwork, refreshKey } = useContext(GlobalContext);

  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const balanceCanAnimate = useHasLoaded(balanceLoaded);

  const fetchAccountData = useCallback(async () => {
    if (multisigAccountId) {
      try {
        const { balance } = await getAccountData(multisigAccountId, connectedNetwork!);
        updateBalance(balance);
      } catch {
        // Mirror call failed (common on custom networks) — ignore,
        // the useEffect below will sync from currentAccount instead
      }
      setBalanceLoaded(true);
    }
  }, [connectedNetwork, multisigAccountId, updateBalance]);

  // Sync sidebar balance from backend-provided wallet data (SDK-based, always reliable)
  useEffect(() => {
    const hbars = currentAccount?.balances?.hbars;
    if (hbars) {
      updateBalance(Number(hbars.replace(/[^0-9.]/g, '') || '0'));
      setBalanceLoaded(true);
    }
  }, [currentAccount?.balances?.hbars, updateBalance]);

  const isScreenLarge = useMediaQuery({ query: `(max-width: ${SCREEN_LG})` });

  const { openModal } = useContext(GlobalContext);

  const menuRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    if (multisigAccountId) {
      fetchAccountData();
    }
  }, [fetchAccountData, multisigAccountId, refreshKey]);

  useEffect(() => {
    const container = document.getElementById('content');
    if (!container) return;

    let offset = 0;
    let rafId: number | null = null;
    let isAnimating = false;

    const animate = () => {
      offset *= 0.91;

      if (Math.abs(offset) < 0.3) {
        offset = 0;
        isAnimating = false;
        if (menuRef.current) {
          menuRef.current.style.transform = 'translateY(0px)';
        }
        return;
      }

      if (menuRef.current) {
        menuRef.current.style.transform = `translateY(${offset}px)`;
      }

      rafId = requestAnimationFrame(animate);
    };

    const handleScroll = () => {
      const currentScroll = container.scrollTop;
      const delta = currentScroll - lastScrollTop.current;
      lastScrollTop.current = currentScroll;

      offset -= delta * 0.3;

      if (!isAnimating) {
        isAnimating = true;
        rafId = requestAnimationFrame(animate);
      }
    };

    lastScrollTop.current = container.scrollTop;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <Flex
      ref={menuRef}
      willChange="transform"
      p={6}
      w={318}
      minW={318}
      gap={4}
      mr={4}
      bg="white"
      borderRadius={16}
      textAlign="center"
      direction="column"
      alignItems="center"
      alignSelf="flex-start"
      position="sticky"
      top={7}
      h="calc(100vh - 64px - 56px)"
      overflowY="auto"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
    >
      <Box width="full" display="flex" flexDirection="column" alignItems="center" gap={2}>
        <HashBasedAvatar UID={getAvatarUID(walletId)} size={64} />
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Skeleton isLoaded={!!currentAccount.name} borderRadius={6} minW="120px" minH="24px">
            <Text size="lg" variant="bodyBold">
              {currentAccount.name}
            </Text>
          </Skeleton>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
            <Box
              as="button"
              display="flex"
              alignItems="center"
              gap={1}
              px={1}
              py={1}
              borderRadius={6}
              cursor="pointer"
              title="Fund wallet"
              transition="background 0.15s"
              _hover={{ bg: 'blackAlpha.100' }}
              onClick={() => openModal(ModalType.FundWalletModal)}
            >
              <WalletFundIcon style={{ width: 18, height: 18 }} />
              <Text fontSize="xs" fontWeight="500">Fund</Text>
            </Box>
            <Box
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={1}
              borderRadius={6}
              cursor="pointer"
              title="Copy address"
              transition="background 0.15s"
              _hover={{ bg: 'blackAlpha.100' }}
              onClick={() => {
                copyToClipboard(multisigAccountId || '');
                showToast('Copied to clipboard', 'success', undefined, 'top');
              }}
            >
              <CopyIcon style={{ width: 18, height: 18 }} />
            </Box>
            {getExplorerUrl(connectedNetwork!, 'account', multisigAccountId!) && (
              <Link
                href={getExplorerUrl(connectedNetwork!, 'account', multisigAccountId!)!}
                cursor="pointer"
                isExternal
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={1}
                borderRadius={6}
                title="View in Explorer"
                transition="background 0.15s"
                _hover={{ bg: 'blackAlpha.100' }}
              >
                <ExternalIcon style={{ width: 18, height: 18 }} />
              </Link>
            )}
          </Box>
        </Box>
        {isScreenLarge
          ? null
          : (
            <Flex gap={0} w="full">
              <Button
                variant="primary"
                borderRadius={6}
                borderRightRadius={0}
                flex={1}
                onClick={() => openModal(ModalType.CreateTransactionWizard)}
                _hover={{ bg: '#2d2d2d', color: 'white', borderColor: 'transparent' }}
                _active={{ bg: '#404040', color: 'white', borderColor: 'transparent' }}
              >
                New Transaction
              </Button>
              <ChakraMenu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  variant="primary"
                  borderRadius={6}
                  borderLeftRadius={0}
                  borderLeft="1px solid rgba(255,255,255,0.2)"
                  minW={10}
                  aria-label="More transaction options"
                  icon={<ArrowDown />}
                  _hover={{ bg: '#2d2d2d', color: 'white', borderColor: 'transparent' }}
                  _active={{ bg: '#404040', color: 'white', borderColor: 'transparent' }}
                />
                <MenuList minW="200px" py={2} borderRadius={8} boxShadow="lg">
                  <MenuItem
                    fontSize="sm"
                    py={2}
                    onClick={() => openModal(ModalType.CreateTransactionWizard, { presetType: 'SEND_TOKENS' })}
                  >
                    Send tokens
                  </MenuItem>
                  <MenuItem
                    fontSize="sm"
                    py={2}
                    onClick={() => openModal(ModalType.CreateTransactionWizard, { presetType: 'TOKEN_ASSOCIATE' })}
                  >
                    Add a token
                  </MenuItem>
                  <MenuItem
                    fontSize="sm"
                    py={2}
                    onClick={() => openModal(ModalType.CreateTransactionWizard, { presetType: 'CONTRACT_INTERACTION' })}
                  >
                    Build a transaction
                  </MenuItem>
                </MenuList>
              </ChakraMenu>
            </Flex>
          )
        }
        <Flex p={4} width="full" bg="primary" alignItems="center" justifyContent="center" gap={1.5}>
          <HederaLogo style={{ width: 20, height: 20 }} />
          <Skeleton isLoaded={balanceLoaded} borderRadius={6} minH="24px">
            <AnimatedNumber value={balance} animate={balanceCanAnimate} size="lg" variant="bodyBold" />
          </Skeleton>
        </Flex>
      </Box>
      <Divider borderColor="rgba(0, 0, 0, 0.3)" my={2} />
      <Box width="full">
        {menuItems.map(menuItem => {
          const Icon = menuItem.selectedIcon
          return (
            <Box
              p={4}
              mr="auto"
              width="full"
              display="flex"
              alignItems="center"
              key={menuItem.name}
              cursor="pointer"
              bg={location.pathname.includes(menuItem.route) ? '#f4f4f4ff' : 'transparent'}
              color="black"
              onClick={() =>
                navigate(
                  `${ApplicationRoutes.Dashboard}/${walletId}/${menuItem.route}/${multisigAccountId}`,
                )
              }
            >
              <Icon />
              <Text ml={2} size="sm" variant="bodyBold">
                {menuItem.name}
              </Text>
            </Box>
          );
        })}
      </Box>
      <Button
        w={237}
        mt="auto"
        variant="secondary"
        borderRadius={6}
        onClick={() => navigate(ApplicationRoutes.Base)}
      >
        <SwitchAccountIcon />
        <Text size="sm" variant="bodyBold" ml={2}>
          Switch Account
        </Text>
      </Button>
    </Flex>
  );
};

export default Menu;
